import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector } from 'redux-form';
import Joi from 'joi-full';
import { sortBy, get, isInteger } from 'lodash';
import { withRouter } from 'react-router';
import { withHandlers, withProps, withPropsOnChange } from 'recompose';
import { t } from '../../../../modules/infra/i18n';
import { syncValidateData } from '../../../common/validator';
import BasicFormField from '../../../common/formFields/basicFormField';
import CancelButtonLink from '../../../common/buttons/cancelButtonLink';
import AdminSaveButton from '../../shared/Buttons/AdminSaveButton';
import AdminDeleteButton from '../../shared/Buttons/AdminDeleteButton';
import { getShops } from '../../shops/reducers';
import TextArea from '../../../common/textarea';
import TabbedForm from '../../../common/TabbedForm';
import { trimFunction } from '../../../common/trimData';
import { getStaffMembers } from '../../users/selectors';
import BrandShopList from './BrandShopList';
import BrandPageForm from './BrandPageForm';
import CustomAppForm from './CustomAppForm';
import { CurrencySelect } from '../../../common/formFields/CurrencySelect';
import CheckboxSwitch from '../../../common/checkboxSwitch';
import LanguageMultiSelectField from '../../../common/LanguageMultiSelect';
import SelectField from '../../../common/ModernSelect';
import { prepareLanguageSettings, preparePrimaryAccountOwner } from '../helpers';
import Tooltip from '../../../common/tooltip';
import KioskLogoControls from './KioskLogoControls';
import { OFFER_PAYOUT_OPTIONS } from '../constants';

const getSchema = () => Joi.object().keys({
  name: Joi.string().required().min(2).max(50)
    .label(t('brand:form.name_label')),
  website: Joi.string().min(2).max(50).allow('', null)
    .label(t('brand:form.website_label')),
  shopId: Joi.array()
    .optional(),
  email: Joi.string().email({ minDomainSegments: 2 }).required()
    .label(t('brand:form.email_label')),
  smsOriginator: Joi.string().optional().allow('', null)
    .label(t('brand:form.sms_originator_label')),
  smsOriginatorVerified: Joi.boolean().optional().allow(null)
    .label(t('brand:form.sms_originator_verified_label')),
  rewardsEnabled: Joi.boolean().optional().allow(null),
  financingOffersDisabled: Joi.boolean().optional().allow(null),
  showBrandCustomersToBarbers: Joi.boolean().optional().allow(null),
  showBrandCustomersToShop: Joi.boolean().optional().allow(null),
  supportedLanguages: Joi.array(),
  settings: Joi.object().keys({
    ecommerceSyncIntegration: Joi.boolean().optional().allow(null),
    mailchimpIntegrationEnabled: Joi.boolean().optional().allow(null),
    membershipsEnabled: Joi.boolean().optional().allow(null),
    itemizedChargeDisplayEnabled: Joi.boolean().optional().allow(null),
    customKioskLogoEnabled: Joi.boolean().optional().allow(null),
    customKioskLogo: Joi.object()
      .when('customKioskLogoEnabled', {
        is: true,
        then: Joi.required().label(t('brand:form.custom_kiosk_logo_label')),
        otherwise: Joi.optional().allow(null),
      }),
    staffManagementEnabled: Joi.boolean().optional().allow(null),
    primaryAccountOwner: Joi.when('staffManagementEnabled', {
      is: Joi.boolean().valid(true).required(),
      then: Joi.alternatives().try(Joi.object(), Joi.string()).required().label(t('brand:form.primary_account_owner_label')),
      otherwise: Joi.object().optional().allow(null),
    }),
    displayReservationAds: Joi.boolean().optional().allow(null),
    offerInstantPayout: Joi.number().optional().allow(null),
  }).optional().allow(null),
});

const validate = values => syncValidateData(values, getSchema());

const renderCancelButton = () => (<CancelButtonLink link="/admin/brands" content={t('buttons.cancel')} />);

const renderSaveButton = () => (
  <AdminSaveButton content={t('brand:form.save_button')} />
);

const renderDeleteButton = (onClick, brandShops) => () => {
  if (brandShops.length > 0) {
    return null;
  }
  return (<AdminDeleteButton
    className="c-btn c-btn--red u-width-120 u-mr- u-float-left"
    onClick={onClick}
  />);
};

const offerIpInputParseFn = (selected) => {
  const isRealOptionSelected = isInteger(get(selected, 'value')) && selected.value !== OFFER_PAYOUT_OPTIONS[0].value;
  return isRealOptionSelected ? selected.value : null;
};

const BrandForm = ({
  handleSubmit,
  initialValues,
  edit,
  onSave,
  onDelete,
  onSortChange,
  brandShops,
  extension,
  onMembershipsEnabledChange,
  onApiReaderEnabledChange,
  supportedLanguages,
  customKioskLogo,
  customKioskLogoEnabled,
  hasStaffManagementEnabled,
  possibleStaffMembers,
}) => (
  <TabbedForm
    className="c-inner-wrapper u-max-width-710 u-mt u-pt0"
    form="brandForm"
    initialValues={initialValues}
    validate={validate}
    onSubmit={handleSubmit(trimFunction(onSave))}
    deleteButton={renderDeleteButton(onDelete, brandShops)}
    cancelButton={renderCancelButton}
    saveButton={renderSaveButton}
    edit={edit}
  >
    <TabbedForm.Tab title={t('brand:form.brand_tab_title')}>
      <div className="c-heading c-heading--lightgrey c-wrapper u-p0 u-mt-- u-mb-" >{t('brand:form.brand_settings_heading')}</div>
      <BasicFormField
        placeholder={t('brand:form.name_label')}
        label={t('brand:form.name_label')}
        name="name"
        maxLength="255"
      />
      <div className="layout__item">
        <div className="c-label-new">{t('brand:form.brand_description_label')}</div>
        <TextArea className="c-textarea" name="description" placeholder={t('brand:form.brand_description_label')} />
      </div>
      <BasicFormField
        placeholder={t('brand:form.email_label')}
        label={t('brand:form.email_label')}
        name="email"
      />
      <BasicFormField
        placeholder={t('brand:form.website_label')}
        label={t('brand:form.website_label')}
        name="website"
      />
      <BasicFormField
        placeholder="Instagram"
        label="Instagram"
        name="instagram"
      />
      <div className="layout__item u-1/2-medium-and-up">
        <div className="c-label-new">{t('brand:form.currency_label')}</div>
        <CurrencySelect name="currency" />
      </div>
      <div className="c-separator u-mb u-mt" />
      <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">{t('brand:form.language_settings_heading')}</div>
      <LanguageMultiSelectField
        label={t('brand:form.supported_languages_label')}
        name="settings.supportedLanguages"
      />
      <SelectField
        label={t('brand:form.brand_default_language_label')}
        name="settings.defaultLanguage"
        options={supportedLanguages}
      />
      <div className="c-separator u-mb u-mt" />
      <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">{t('brand:form.sms_configuration_heading')}</div>
      <BasicFormField
        placeholder={t('brand:form.sms_originator_placeholder')}
        label={t('brand:form.sms_originator_label')}
        name="smsOriginator"
        normalize={value => value === '' ? null : value}
      />
      <CheckboxSwitch
        name="smsOriginatorVerified"
        id="smsOriginatorVerified"
        label={t('brand:form.sms_originator_verified_label')}
        wrapperClass="layout__item"
      />
      <div className="c-separator u-mb u-mt" />
      <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">{t('brand:form.options_heading')}</div>
      <div className="u-mb u-pl o-flexbox--justify-start o-flexbox o-flexbox--content-align-center">
        <CheckboxSwitch
          name="settings.ecommerceSyncIntegration"
          id="settings.ecommerceSyncIntegration"
          label={t('brand:form.ecommerce_integration_label')}
          wrapperClass=""
        />
        <Tooltip
          className="c-info-tooltip-new--small"
          text={t('brand:form.ecommerce_integration_tooltip')}
        />
      </div>
      <CheckboxSwitch
        name="settings.mailchimpIntegrationEnabled"
        id="settings.mailchimpIntegrationEnabled"
        label={t('brand:form.mailchimp_integration_label')}
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="rewardsEnabled"
        id="rewardsEnabled"
        label={t('brand:form.loyalty_program_label')}
        wrapperClass="layout__item"
      />
      <div className="u-mb u-pl o-flexbox--justify-start o-flexbox o-flexbox--content-align-center">
        <CheckboxSwitch
          name="settings.staffManagementEnabled"
          id="settings.staffManagementEnabled"
          label={t('brand:form.staff_management_label')}
          wrapperClass=""
        />
        <Tooltip
          className="c-info-tooltip-new--small"
          text={t('brand:form.staff_management_tooltip')}
        />
      </div>
      <SelectField
        display-if={hasStaffManagementEnabled}
        label={t('brand:form.primary_account_owner_label')}
        name="settings.primaryAccountOwner"
        id="settings.primaryAccountOwner"
        options={possibleStaffMembers}
      />
      <CheckboxSwitch
        name="showBrandCustomersToShops"
        id="showBrandCustomersToShops"
        label={t('brand:form.show_brand_customers_to_shops_label')}
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="showBrandCustomersToBarbers"
        id="showBrandCustomersToBarbers"
        label={t('brand:form.show_brand_customers_to_barbers_label')}
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="overrideDefaultNotificationsConsent"
        id="overrideDefaultNotificationsConsent"
        label={t('brand:form.override_default_notifications_label')}
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="financingOffersDisabled"
        id="financingOffersDisabled"
        label={t('brand:form.disable_loans_label')}
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="apiReaderEnabled"
        id="apiReaderEnabled"
        label={t('brand:form.enable_api_based_readers')}
        wrapperClass="layout__item"
        onChange={onApiReaderEnabledChange}
      />
      <CheckboxSwitch
        name="hideGiftcardCodeFromReceipt"
        id="hideGiftcardCodeFromReceipt"
        label={t('brand:form.hide_giftcard_codes_from_receipt')}
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="settings.membershipsEnabled"
        id="settings.membershipsEnabled"
        label={t('brand:form.memberships_label')}
        wrapperClass="layout__item"
        onChange={onMembershipsEnabledChange}
      />
      <div className="u-mb u-pl o-flexbox--justify-start o-flexbox o-flexbox--content-align-center">
        <CheckboxSwitch
          name="settings.itemizedChargeDisplayEnabled"
          id="settings.itemizedChargeDisplayEnabled"
          label={t('brand:form.itemized_charge_display_on_kiosk')}
        />
        <Tooltip
          className="c-info-tooltip-new"
          text={t('brand:form.itemized_charge_display_tooltip')}
        />
      </div>
      <div className="u-mb u-pl">
        <KioskLogoControls
          customKioskLogo={customKioskLogo}
          customKioskLogoEnabled={customKioskLogoEnabled}
        />
      </div>
      <div className="u-mb u-pl o-flexbox--justify-start o-flexbox o-flexbox--content-align-center">
        <CheckboxSwitch
          name="settings.displayReservationAds"
          id="settings.displayReservationAds"
          label={t('brand:form.ads_on_reservation_page')}
        />
        <Tooltip
          className="c-info-tooltip-new"
          text={t('brand:form.ads_on_reservation_page_tooltip')}
        />
      </div>
      <div>
        <div className="c-separator u-mb u-mt" />
        <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">{t('brand:form.rapid_transfer_heading')}</div>
        <SelectField
          label={<>{t('brand:form.offered_after_label')} <Tooltip
            className="c-info-tooltip-new u-type--no-transform"
            text={t('brand:form.offered_after_tooltip')}
          /></>}
          name="settings.offerInstantPayout"
          options={OFFER_PAYOUT_OPTIONS.map(option => ({
            ...option,
            label: option.days !== undefined
              ? t(option.labelKey, { days: option.days, transactions: option.transactions })
              : t(option.labelKey)
          }))}
          format={(value) => {
            const option = OFFER_PAYOUT_OPTIONS.find(o => o.value === value) || OFFER_PAYOUT_OPTIONS[0];
            return {
              ...option,
              label: option.days !== undefined
                ? t(option.labelKey, { days: option.days, transactions: option.transactions })
                : t(option.labelKey)
            };
          }}
          normalize={selected => selected.value}
          defaultValue={OFFER_PAYOUT_OPTIONS[0]}
        />
        <div className="c-separator u-mb u-mt" />
      </div>
      <div>
        <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">{t('brand:form.ad_conversion_tracking_heading')}</div>

        <BasicFormField
          placeholder={t('brand:form.ga_measurement_id_placeholder')}
          label={t('brand:form.ga_measurement_id_label')}
          name="settings.gaMeasurementId"
        />

        <BasicFormField
          placeholder={t('brand:form.ga_access_token_placeholder')}
          label={t('brand:form.ga_access_token_label')}
          name="settings.gaMeasurementSecret"
        />

        <BasicFormField placeholder={t('brand:form.meta_pixel_id_placeholder')} label={t('brand:form.meta_pixel_id_label')} name="settings.metaPixelId" />

        <div className="c-separator u-mb u-mt" />
      </div>
      <div className="layout__item">
        <div className="c-label-new">{t('brand:form.brand_shop_configuration_label')}</div>
        <BrandShopList name="brandShops" brandShops={brandShops} onSortChange={onSortChange} />
      </div>
    </TabbedForm.Tab>
    <TabbedForm.Tab title={t('brand:form.web_brand_page_tab')} display-if={edit}>
      <BrandPageForm extension={extension || undefined} />
    </TabbedForm.Tab>
    <TabbedForm.Tab title={t('brand:form.custom_app_tab')} display-if={edit}>
      <CustomAppForm extension={extension || undefined} />
    </TabbedForm.Tab>
  </TabbedForm>
);

BrandForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  edit: PropTypes.bool,
  shops: PropTypes.array, // eslint-disable-line
  brandShops: PropTypes.array, // eslint-disable-line
  extension: PropTypes.object, // eslint-disable-line
  onMembershipsEnabledChange: PropTypes.func.isRequired,
  onApiReaderEnabledChange: PropTypes.func.isRequired,
  customKioskLogo: PropTypes.bool.isRequired,
  customKioskLogoEnabled: PropTypes.bool.isRequired,
  hasStaffManagementEnabled: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  possibleStaffMembers: PropTypes.array.isRequired,
};

BrandForm.defaultProps = {
  edit: false,
};

const selector = formValueSelector('brandForm');
const mapStateToProps = state => ({
  shops: getShops(state),
  brandShops: sortBy(selector(state, 'brandShops'), ['shopOrder']),
  possibleStaffMembers: getStaffMembers(state),
  extension: selector(state, 'extension'),
  supportedLanguages: selector(state, 'settings.supportedLanguages'),
  defaultLanguage: selector(state, 'settings.defaultLanguage'),
  customKioskLogo: selector(state, 'settings.customKioskLogo'),
  customKioskLogoEnabled: selector(state, 'settings.customKioskLogoEnabled'),
  hasStaffManagementEnabled: selector(state, 'settings.staffManagementEnabled'),
  selectedPrimaryAccountOwnerOwner: selector(state, 'settings.primaryAccountOwner'),
});

const getPossiblePrimaryAccounts = (possibleStaffMembers, brandShops) => {
  if (!Array.isArray(possibleStaffMembers)) {
    return [];
  }

  if (brandShops.length === 1) {
    return possibleStaffMembers;
  }

  return possibleStaffMembers.filter(({ brandId }) => !!brandId);
};

const hasInitialPrimaryAccountOwnerChanged = (prevProps, nextProps) =>
  get(prevProps, 'initialValues.settings.primaryAccountOwner') !==
    get(nextProps, 'initialValues.settings.primaryAccountOwner');

const hasPossibleStaffMembersArrayLengthChanged = (prevProps, nextProps) =>
  get(prevProps, 'possibleStaffMembers.length') !== get(nextProps, 'possibleStaffMembers.length');

const hasSelectedPrimaryAccountOwnerChangedToString = (prevProps, nextProps) => (
  prevProps.selectedPrimaryAccountOwnerOwner !== nextProps.selectedPrimaryAccountOwnerOwner
  && typeof nextProps.selectedPrimaryAccountOwnerOwner === 'string'
);

export default compose(
  withRouter,
  connect(mapStateToProps),
  reduxForm({
    form: 'brandForm',
    validate,
  }),
  withProps(props => ({
    possibleStaffMembers: getPossiblePrimaryAccounts(props.possibleStaffMembers, props.brandShops)
      .map(({ id, username }) => ({
        label: username,
        value: id,
      })),
  })),
  withPropsOnChange(
    (prevProps, nextProps) =>
      // eslint-disable-next-line max-len
      hasInitialPrimaryAccountOwnerChanged(prevProps, nextProps)
      || hasPossibleStaffMembersArrayLengthChanged(prevProps, nextProps)
      || hasSelectedPrimaryAccountOwnerChangedToString(prevProps, nextProps),

    (props) => {
      if (props.initialValues.settings.primaryAccountOwner) {
        const accountOwner = props.possibleStaffMembers
          .find(({ value }) => value === props.initialValues.settings.primaryAccountOwner);

        if (accountOwner) {
          props.change('settings.primaryAccountOwner', accountOwner);
        }
      }
    },
  ),
  withPropsOnChange(['supportedLanguages'], (props) => {
    if (!(props.supportedLanguages || []).includes(props.defaultLanguage)) {
      props.change('settings.defaultLanguage', (props.supportedLanguages || [])[0]);
    }
  }),
  withHandlers({
    onSave: props => (data) => {
      return props.onSave(preparePrimaryAccountOwner(prepareLanguageSettings(data)));
    },
  }),
)(BrandForm);
